import { PlanungModules } from '@bp/pim-auth-constants';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChangeViewIcon,
  ControlIcon,
  DoubleExclamationmarkIcon,
  FileIcon,
  FullscreenActiveIcon,
  FullscreenIcon,
  GenerateIcon,
  ImportIcon,
  ImproveIcon,
  InfoIcon,
  PrintIcon,
  RoomIcon,
  SpinnerIcon,
  TestIcon,
  TimetableIcon,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContainerDimensions } from '../../../hooks/useDimensions';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { GridType } from '../../../pages/Timetable/Plan/TimetableVersion/Board/types';
import { HighlightType } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { testTimetableVersion } from '../../../utils/timetable/timetableVersion';
import { useAscImport } from '../../AscImport/hooks/useAscImport';
import { useLessonStore } from '../../Lessons/TimetableVersion/TimetableVersionLessonProvider';
import { LicenseLock, showLicenseHint } from '../../LicenseHint/LicenseHint';
import { MenuBar, MenuBarEntry, Subaction } from '../../MenuBar/MenuBar';
import { Slider } from '../../Slider/Slider';
import { BetaChip } from '../../Solver/BetaChip';
import { OmniChip } from '../../Solver/OmniChip';
import { LegendModal } from '../LegendModal/LegendModal';
import { useTimetableStore } from '../TimetableProvider';
import styles from './GridMenuBar.module.scss';

export const GridMenuBar: FC = observer(() => {
  const pinboardStore = useTimetableStore();
  const lessonStore = useLessonStore();
  const userConfig = useUserConfigContext();
  const { t } = useTranslation();
  const permissions = usePermissionChecker();
  // Timefold-Analyse ist OmniAdmin-only — die Aktion wird sonst gar nicht erst angeboten.
  const isOmniAdmin = permissions?.isOmniAdmin() ?? false;
  // Ohne gebuchten Solver bzw. Stundenplan: Generator und aSc-Export zeigen den Buchungshinweis (Schloss statt
  // „Beta“), Drucken trägt „MUSTER“.
  const solverLicensed = permissions?.isSolverEnabled() ?? true;
  const timetableLicensed = permissions?.isTimetableEnabled() ?? true;
  const solverBadge = solverLicensed ? <BetaChip /> : <LicenseLock hint={{ kind: 'solver' }} />;

  // While a solver job runs, all destructive board actions are blocked (the run owns the plan).
  const solving = pinboardStore.solverStore.jobRunning;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { width } = useContainerDimensions(wrapperRef);
  const isCompact = width < 1000;

  const [escFullscreenExit, setEscFullscreenExit] = useState<boolean>(false);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [legendOpen, setLegendOpen] = useState<boolean>(false);
  const [ascExporting, setAscExporting] = useState<boolean>(false);

  const { pickAscFile, ascBusy, ascImportDialog } = useAscImport({
    versionUuid: pinboardStore.getCurrentVersion()?.uuid,
    // Der Import legt Unterrichte an und löscht sie — beides läuft per REST, urql bekommt davon
    // nichts mit. Board-Karten UND Unterrichtsliste müssen deshalb explizit neu geladen werden,
    // sonst zeigt die Liste gelöschte Unterrichte weiter an.
    onImported: () => {
      pinboardStore.reloadVersionData();
      lessonStore.reloadLessons();
    },
  });

  function onChangeView(value: GridType) {
    // Bewusste Auswahl: selectContext speichert sie in der UserConfig.
    pinboardStore.selectContext(value);
  }

  function onChangeHighlightMode(highlight: HighlightType) {
    pinboardStore.setHighlightMode(highlight);
  }

  function onFullscreen() {
    const grid = document.querySelector('#timetable-grid');
    if (!grid) return;
    if (!document.fullscreenElement) {
      grid
        .requestFullscreen()
        .then(() => {
          pinboardStore.setIsFullscreen(true);
          document.body.classList.add('fullscreen');
        })
        .catch((err) => {
          console.log(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
        });
    } else {
      pinboardStore.setIsFullscreen(false);
      document.body.classList.remove('fullscreen');
      void document.exitFullscreen();
    }
  }

  async function timefoldAnalyzeTimetable() {
    if (!pinboardStore.getCurrentVersion()?.uuid) {
      return;
    }
    // The run itself (incl. solve-option parity, P0-5) lives in the store so the re-run banner shares it.
    if (!pinboardStore.timefoldAnalyzeMode.isActive) {
      await pinboardStore.runTimefoldCheck();
    }
    pinboardStore.setTimefoldAnalyzeModeActive(!pinboardStore.timefoldAnalyzeMode.isActive);
  }

  async function controlTimetable() {
    if (!pinboardStore.getCurrentVersion()?.uuid) {
      return;
    }
    if (!pinboardStore.controlMode.isActive) {
      await pinboardStore.runControlCheck();
    }
    pinboardStore.setControlModeActive(!pinboardStore.controlMode.isActive);
  }

  async function testTimetable(generatorMode: boolean = false) {
    const versionUuid = pinboardStore.getCurrentVersion()?.uuid;

    if (versionUuid) {
      void pinboardStore.setTestModalFromGenerator(generatorMode);
      void pinboardStore.setTestModalOpen(true);
      void pinboardStore.setTestModalLoading(true);
      const testResult = await testTimetableVersion(
        versionUuid,
        pinboardStore.context === 'classes' && pinboardStore.selectedRows.length > 0
          ? pinboardStore.selectedRows.map((row) => row.value)
          : pinboardStore.getClassRows().map((row) => row.value),
      );
      if (generatorMode && testResult.length === 0) {
        void pinboardStore.setTestModalResult([]);
        void pinboardStore.setTestModalOpen(false);
        void pinboardStore.setTestModalFromGenerator(false);
        return;
      }
      void pinboardStore.setTestModalResult(testResult);
      void pinboardStore.setTestModalLoading(false);
    }
  }

  function generateTimetable() {
    if (!solverLicensed) {
      showLicenseHint({ kind: 'solver' });
      return;
    }
    // Wizard step 1 owns the preflight test now — just open the modal.
    pinboardStore.solverStore.setTask('GENERATE');
    pinboardStore.solverStore.setWizardStep(1);
    pinboardStore.solverStore.setPreflightResult(null);
    pinboardStore.solverStore.setSolverWizardOpen(true);
  }

  // OPTIMIZE / ASSIGN_ROOMS skip the preflight — open the wizard straight on the options slide.
  function improveTimetable() {
    if (!solverLicensed) {
      showLicenseHint({ kind: 'solver' });
      return;
    }
    pinboardStore.solverStore.setTask('OPTIMIZE');
    pinboardStore.solverStore.setWizardStep(2);
    pinboardStore.solverStore.setSolverWizardOpen(true);
  }

  function assignRoomsTimetable() {
    if (!solverLicensed) {
      showLicenseHint({ kind: 'solver' });
      return;
    }
    pinboardStore.solverStore.setTask('ASSIGN_ROOMS');
    pinboardStore.solverStore.setWizardStep(2);
    pinboardStore.solverStore.setSolverWizardOpen(true);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies(pinboardStore.setIsFullscreen): necessary
  useEffect(() => {
    const fullscreenchanged = () => {
      if (escFullscreenExit) {
        document.body.classList.remove('fullscreen');
        pinboardStore.setIsFullscreen(false);
      }
      setEscFullscreenExit(!escFullscreenExit);
    };
    document.addEventListener('fullscreenchange', fullscreenchanged);
    return () => {
      document.removeEventListener('fullscreenchange', fullscreenchanged);
    };
  }, [escFullscreenExit]);

  const noop = () => undefined;

  const printActions: MenuBarEntry[] = [
    {
      actions: [
        {
          icon: isPrinting ? <SpinnerIcon className='svg-icon spinning' /> : <PrintIcon />,
          title: t('pinboard.actions.print'),
          badge: timetableLicensed ? undefined : <LicenseLock hint={{ kind: 'watermark' }} />,
          disabled: isPrinting,
          action: async () => {
            setIsPrinting(true);
            try {
              await pinboardStore.downloadPdf();
            } finally {
              setIsPrinting(false);
            }
          },
        },
        // Fremdformate unter einem Dach: aSc-XML exportieren, extern bearbeiten, wieder einlesen.
        // Beide Richtungen laufen direkt gegen die aktuelle Version — ohne Solver-Job.
        {
          icon: ascExporting || ascBusy ? <SpinnerIcon className='svg-icon spinning' /> : <FileIcon />,
          title: t('pinboard.actions.sectionInterfaces'),
          action: noop,
          subactions: [
            {
              title: t('pinboard.actions.ascExport'),
              disabled: ascExporting,
              action: async () => {
                if (!timetableLicensed) {
                  showLicenseHint({ kind: 'export', module: PlanungModules.timetable });
                  return;
                }
                setAscExporting(true);
                try {
                  await pinboardStore.downloadAscExport();
                } finally {
                  setAscExporting(false);
                }
              },
            },
            {
              title: t('pinboard.actions.ascImport'),
              disabled: ascBusy || solving || !pinboardStore.isEditable,
              action: () => pickAscFile(),
            },
          ],
        },
        {
          icon: <InfoIcon />,
          title: t('legend.open'),
          action: () => setLegendOpen(true),
        },
      ],
    },
  ];

  // ── "Anzeige ändern": Abschnitte als Daten, damit sie je nach Platz flach (Top-Level),
  //    als ein Dropdown je Abschnitt oder als ein gemeinsames Dropdown gerendert werden können.
  const ruler: Subaction = { title: null, action: null, type: 'ruler' };
  const rowHeightNode: Subaction = {
    type: 'component',
    node: (
      <>
        <div
          className={'mr-2'}
          onClick={() => {
            pinboardStore.setDefaultRowHeight();
          }}
        >
          {t('pinboard.actions.rowHeightLabel')}
        </div>
        <Slider
          value={[pinboardStore.rowHeight]}
          min={30}
          max={75}
          step={1}
          onValueChange={(value) => {
            pinboardStore.setRowHeight(value[0]);
          }}
        />
      </>
    ),
  };
  const showAllRowsItem = {
    title: t('pinboard.actions.showAllRows', { count: pinboardStore.hiddenRows.size }),
    action: () => pinboardStore.showAllRows(),
  };

  const heading = (title: string): Subaction => ({ type: 'heading', title });
  const toggle = (title: string, action: () => void, value: boolean, disabled?: boolean): Subaction => ({
    title,
    action,
    type: 'switch',
    value,
    disabled,
  });

  // "Ansicht" = was auf dem Board steht: zuerst der Modus (exklusive Auswahl), darunter die
  // Schalter, die auf diesem Modus liegen — nach Wirkung gruppiert, nicht nach Herkunft.
  const viewSubs: Subaction[] = [
    { title: t('pinboard.actions.viewClass'), action: () => onChangeView('classes') },
    { title: t('pinboard.actions.viewTeacher'), action: () => onChangeView('teachers') },
    { title: t('pinboard.actions.viewRoom'), action: () => onChangeView('rooms') },
    { title: t('pinboard.actions.viewSubject'), action: () => onChangeView('subjects'), disabled: true },
    ruler,
    heading(t('pinboard.actions.sectionHighlights')),
    toggle(
      t('pinboard.actions.showWarnings'),
      () => onChangeHighlightMode('warnings'),
      pinboardStore.highlightMode.includes('warnings'),
    ),
    toggle(
      t('pinboard.actions.highlightConflicts'),
      () => onChangeHighlightMode('conflicts'),
      pinboardStore.highlightMode.includes('conflicts'),
    ),
    toggle(
      t('pinboard.actions.showEpochRanges'),
      () => pinboardStore.setShowEpochRanges(!pinboardStore.showEpochRanges),
      pinboardStore.showEpochRanges,
    ),
    toggle(
      t('pinboard.actions.showNoRooms'),
      () => onChangeHighlightMode('rooms'),
      pinboardStore.highlightMode.includes('rooms'),
    ),
    ruler,
    heading(t('pinboard.actions.sectionCards')),
    toggle(
      t('pinboard.actions.showSubjectColor'),
      () => pinboardStore.setCardColorBySubject(!pinboardStore.cardColorBySubject),
      pinboardStore.cardColorBySubject,
    ),
    toggle(
      t('pinboard.actions.showTeacherInsteadOfSubject'),
      () => pinboardStore.setShowTeacherInsteadOfSubject(!pinboardStore.showTeacherInsteadOfSubject),
      pinboardStore.showTeacherInsteadOfSubject,
    ),
    toggle(
      t('pinboard.actions.hideIcons'),
      () => onChangeHighlightMode('noIcons'),
      pinboardStore.highlightMode.includes('noIcons'),
    ),
    toggle(t('pinboard.actions.showCardInfo'), () => pinboardStore.toggleShowCardInfo(), pinboardStore.showCardInfo),
    ruler,
    heading(t('pinboard.actions.sectionFilter')),
    toggle(
      t('pinboard.actions.filterFunctionClasses'),
      () => pinboardStore.setFilterFunctionClasses(!pinboardStore.filterFunctionClasses),
      pinboardStore.filterFunctionClasses,
    ),
    toggle(
      t('pinboard.actions.groupRoomsByBuilding'),
      () => pinboardStore.setGroupRoomsByBuilding(!pinboardStore.groupRoomsByBuilding),
      pinboardStore.groupRoomsByBuilding,
      pinboardStore.context !== 'rooms',
    ),
  ];

  // "Layout" = wie die Arbeitsfläche aufgebaut ist (Panels, Raster) — unabhängig vom Planinhalt.
  const layoutSubs: Subaction[] = [
    toggle(
      t('pinboard.actions.sideBySideLayout'),
      () => pinboardStore.toggleBoardLayout(),
      pinboardStore.boardLayout === 'split',
    ),
    toggle(
      t('pinboard.actions.showPreviewMap'),
      () => pinboardStore.toggleShowPreviewMap(),
      pinboardStore.showPreviewMap,
    ),
    toggle(
      t('pinboard.actions.showTimetablePreview'),
      () => pinboardStore.toggleShowTimetablePreview(),
      pinboardStore.showTimetablePreview,
    ),
    ruler,
    rowHeightNode,
    { title: showAllRowsItem.title, action: showAllRowsItem.action },
    ruler,
    {
      // Setzt die gespeicherten Ansichtseinstellungen dieses Boards zurueck; die
      // offene Ansicht uebernimmt das sofort ueber die Observables.
      title: t('pinboard.actions.resetView'),
      action: () => void userConfig.resetSettings(['pinboard']),
    },
  ];

  const fullscreenGroup: MenuBarEntry = {
    actions: [
      {
        icon: pinboardStore.isFullscreen ? <FullscreenActiveIcon /> : <FullscreenIcon />,
        title: pinboardStore.isFullscreen ? t('common.fullscreenCancel') : t('common.fullscreen'),
        action: () => onFullscreen(),
      },
    ],
  };

  // Wenig Platz: beides in einem Dropdown, Layout dort als eigener Abschnitt.
  const singleViewGroup: MenuBarEntry = {
    actions: [
      {
        icon: <ChangeViewIcon />,
        title: t('pinboard.actions.changeView'),
        action: noop,
        subactions: [...viewSubs, ruler, heading(t('pinboard.actions.sectionLayout')), ...layoutSubs],
      },
    ],
  };

  // Genug Platz: "Ansicht" (Inhalt des Boards) und "Layout" (Arbeitsfläche) getrennt.
  const sectionViewGroup: MenuBarEntry = {
    actions: [
      { icon: <ChangeViewIcon />, title: t('pinboard.actions.sectionView'), action: noop, subactions: viewSubs },
      { icon: <TimetableIcon />, title: t('pinboard.actions.sectionLayout'), action: noop, subactions: layoutSubs },
    ],
  };

  // Genug Platz → ein aufgeräumtes Dropdown je Abschnitt; sonst ein gemeinsames Dropdown.
  const viewEntries: MenuBarEntry[] =
    width >= 1250 ? [sectionViewGroup, fullscreenGroup] : [singleViewGroup, fullscreenGroup];

  const compactEntries: MenuBarEntry[] = [
    {
      actions: [
        {
          icon: <TimetableIcon />,
          title: t('common.additionalActions'),
          action: () => {
            // no-op
          },
          subactions: [
            {
              // Compact menu items are plain dropdown labels (no chip node), so the Beta marker
              // rides along as a text suffix here while the full toolbar uses the Chip.
              title: solverLicensed ? `${t('pinboard.actions.generate')} (Beta)` : t('pinboard.actions.generate'),
              action: () => generateTimetable(),
              disabled: pinboardStore.freeCardsSet.size === 0 || !pinboardStore.isEditable,
            },
            // improve, assignRoom und Timefold-Analyse nur für OmniAdmin.
            ...(isOmniAdmin
              ? [
                  {
                    title: `${t('pinboard.actions.improve')} (Omni)`,
                    action: () => improveTimetable(),
                    disabled: !pinboardStore.isEditable || solving,
                  },
                  {
                    title: `${t('pinboard.actions.assignRoom')} (Omni)`,
                    action: () => assignRoomsTimetable(),
                    disabled: !pinboardStore.isEditable || solving,
                  },
                  {
                    title: `${t('pinboard.timefoldAnalyze.action')} (Omni)`,
                    action: () => {
                      void timefoldAnalyzeTimetable();
                    },
                  },
                ]
              : []),
            {
              title: t('pinboard.actions.test'),
              action: () => testTimetable(),
              disabled: solving,
            },
            {
              title: t('pinboard.actions.control'),
              action: () => controlTimetable(),
              disabled: solving,
            },
            {
              title: t('pinboard.actions.conflictList'),
              action: () => pinboardStore.setConflictListModalOpen(true),
            },
          ],
        },
      ],
    },
    singleViewGroup,
    fullscreenGroup,
    ...printActions,
  ];

  const entries: MenuBarEntry[] = [
    {
      actions: [
        {
          icon: <GenerateIcon />,
          title: t('pinboard.actions.generate'),
          badge: solverBadge,
          action: () => generateTimetable(),
          disabled: pinboardStore.freeCardsSet.size === 0 || !pinboardStore.isEditable,
        },
        ...(isOmniAdmin
          ? [
              {
                icon: <ImproveIcon />,
                title: t('pinboard.actions.improve'),
                badge: <OmniChip />,
                action: () => improveTimetable(),
                disabled: !pinboardStore.isEditable || solving,
              },
            ]
          : []),
      ],
    },
    {
      actions: [
        // Timefold-Analyse nur für OmniAdmin (Omni-Chip statt Beta).
        ...(isOmniAdmin
          ? [
              {
                icon: <ImproveIcon />,
                title: t('pinboard.timefoldAnalyze.action'),
                badge: <OmniChip />,
                action: () => {
                  void timefoldAnalyzeTimetable();
                },
              },
            ]
          : []),
        {
          icon: <RoomIcon />,
          title: t('pinboard.actions.assignRoom'),
          badge: solverBadge,
          action: () => assignRoomsTimetable(),
          disabled: !pinboardStore.isEditable || solving,
        },
        {
          icon: <TestIcon />,
          title: t('pinboard.actions.test'),
          action: () => testTimetable(),
          disabled: solving,
        },
        {
          icon: <ControlIcon />,
          title: t('pinboard.actions.control'),
          action: () => controlTimetable(),
          disabled: solving,
        },
        {
          icon: <DoubleExclamationmarkIcon />,
          title: t('pinboard.actions.conflictList'),
          action: () => pinboardStore.setConflictListModalOpen(true),
        },
      ],
    },
    ...viewEntries,
    ...printActions,
  ];

  const timefoldAnalyzeModeEntries: MenuBarEntry[] = [
    {
      actions: [
        {
          icon: <ImproveIcon />,
          title: t('pinboard.timefoldAnalyze.quit'),
          action: () => {
            void timefoldAnalyzeTimetable();
          },
        },
      ],
    },
    {
      actions: [
        {
          icon: <ArrowDownIcon />,
          title: t('pinboard.timefoldAnalyze.expandAll'),
          action: () => pinboardStore.expandTimefoldAnalyzeResults(true),
        },
        {
          icon: <ArrowUpIcon />,
          title: t('pinboard.timefoldAnalyze.collapseAll'),
          action: () => pinboardStore.expandTimefoldAnalyzeResults(false),
        },
      ],
    },
    singleViewGroup,
    fullscreenGroup,
  ];

  const controlModeEntries: MenuBarEntry[] = [
    {
      actions: [
        {
          icon: <ControlIcon />,
          title: t('pinboard.control.quit'),
          action: () => controlTimetable(),
        },
      ],
    },
    {
      actions: [
        {
          icon: <ArrowDownIcon />,
          title: t('pinboard.control.expandAll'),
          action: () => pinboardStore.expandControlResults(true),
        },
        {
          icon: <ArrowUpIcon />,
          title: t('pinboard.control.collapseAll'),
          action: () => pinboardStore.expandControlResults(false),
        },
      ],
    },
    singleViewGroup,
    fullscreenGroup,
  ];

  return (
    <div className={styles['grid-menu-bar']} ref={wrapperRef}>
      <MenuBar
        entries={
          pinboardStore.controlMode.isActive
            ? controlModeEntries
            : pinboardStore.timefoldAnalyzeMode.isActive
              ? timefoldAnalyzeModeEntries
              : isCompact
                ? compactEntries
                : entries
        }
        usePortal={!pinboardStore.isFullscreen}
        passive={pinboardStore.controlMode.isActive || pinboardStore.timefoldAnalyzeMode.isActive}
      />
      <LegendModal isOpen={legendOpen} onClose={() => setLegendOpen(false)} />
      {ascImportDialog}
    </div>
  );
});
